/**
 * Task #3821 — Skills del PA cross-provider (playbook di procedure).
 *
 * DTO condivisi BE↔FE (Zod + z.infer) per l'entità Skill del Personal
 * Assistant: UNA sola entità locale (markdown = fonte di verità) iniettata nel
 * contesto di sistema su QUALSIASI provider e, su agenti Claude, promuovibile
 * opzionalmente a Claude Skill nativa (`container.skills`).
 *
 * CONFINE CONCETTUALE (skill ≠ memoria): la memoria PA è appresa
 * implicitamente; la skill è una procedura ESPLICITA, editabile, versionata e
 * approvata dall'utente. I due mondi non si sovrappongono mai.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Enums / vincoli
// ---------------------------------------------------------------------------
export const PaSkillStatusSchema = z.enum(['draft', 'active']);
export type PaSkillStatus = z.infer<typeof PaSkillStatusSchema>;

export const PaSkillVisibilitySchema = z.enum(['personal', 'shared']);
export type PaSkillVisibility = z.infer<typeof PaSkillVisibilitySchema>;

export const PaSkillProvenanceSchema = z.enum(['workspace', 'chat']);
export type PaSkillProvenance = z.infer<typeof PaSkillProvenanceSchema>;

export const PaSkillSyncStatusSchema = z.enum(['none', 'synced', 'error']);
export type PaSkillSyncStatus = z.infer<typeof PaSkillSyncStatusSchema>;

/** Stato di una skill in una conversazione (mai auto-attivata se disabled). */
export const PaConversationSkillStateValueSchema = z.enum(['active', 'disabled']);
export type PaConversationSkillStateValue = z.infer<typeof PaConversationSkillStateValueSchema>;

/** Come una skill è stata attivata nel turno: esplicito (/skill), stato di
 * conversazione, o aggancio autonomo per pertinenza (sempre dichiarato). */
export const PaSkillActivationModeSchema = z.enum(['explicit', 'conversation', 'auto']);
export type PaSkillActivationMode = z.infer<typeof PaSkillActivationModeSchema>;

/** Nome slug-like usato dal comando `/skill nome`. */
export const PA_SKILL_NAME_REGEX = /^[a-z0-9][a-z0-9_-]{1,63}$/;

// Cap non negoziabili dell'iniezione provider-agnostica (testati lato BE).
/** Dimensione massima del contenuto markdown di una singola skill. */
export const PA_SKILL_MAX_CONTENT_CHARS = 24_000;
/** Numero massimo di skill attive iniettate in un singolo turno. */
export const PA_SKILL_MAX_ACTIVE_PER_TURN = 6;
/** Budget caratteri complessivo del blocco skill nel contesto di sistema. */
export const PA_SKILL_CONTEXT_CHAR_BUDGET = 32_000;

// ---------------------------------------------------------------------------
// Entità
// ---------------------------------------------------------------------------
export const PaSkillSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  content: z.string(),
  version: z.number().int(),
  status: PaSkillStatusSchema,
  visibility: PaSkillVisibilitySchema,
  provenance: PaSkillProvenanceSchema,
  /** Contratto skill→Progetto (Task #3822): solo contratto dati. */
  projectId: z.string().uuid().nullable(),
  ownerUserId: z.string().uuid(),
  /** true se l'utente corrente è l'owner (può modificare/eliminare). */
  isOwner: z.boolean(),
  /** Ponte Claude nativo: id skill Anthropic quando promossa. */
  anthropicSkillId: z.string().nullable(),
  syncStatus: PaSkillSyncStatusSchema,
  /** Errore di sync MAI inghiottito: visibile all'utente. */
  syncError: z.string().nullable(),
  syncedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PaSkill = z.infer<typeof PaSkillSchema>;

/** Claude Skill assegnata dall'admin via config provider: nella sidebar PA è
 * visibile in SOLA LETTURA ("gestita dall'amministratore"). */
export const PaAdminSkillSchema = z.object({
  id: z.string(),
  anthropicSkillId: z.string(),
  displayTitle: z.string(),
  description: z.string().nullable(),
  latestVersion: z.string().nullable(),
});
export type PaAdminSkill = z.infer<typeof PaAdminSkillSchema>;

export const PaConversationSkillStateSchema = z.object({
  skillId: z.string().uuid(),
  state: PaConversationSkillStateValueSchema,
  source: z.enum(['explicit', 'auto']),
});
export type PaConversationSkillState = z.infer<typeof PaConversationSkillStateSchema>;

// ---------------------------------------------------------------------------
// Response DTOs
// ---------------------------------------------------------------------------
export const PaSkillListResponseSchema = z.object({
  skills: z.array(PaSkillSchema),
  /** Popolato solo quando l'assistente gira su Claude; altrimenti []. */
  adminSkills: z.array(PaAdminSkillSchema),
});
export type PaSkillListResponse = z.infer<typeof PaSkillListResponseSchema>;

export const PaConversationSkillsResponseSchema = z.object({
  states: z.array(PaConversationSkillStateSchema),
});
export type PaConversationSkillsResponse = z.infer<typeof PaConversationSkillsResponseSchema>;

// ---------------------------------------------------------------------------
// Request DTOs
// ---------------------------------------------------------------------------
export const PaSkillCreateRequestSchema = z.object({
  name: z.string().regex(PA_SKILL_NAME_REGEX, 'Nome skill non valido: minuscole, cifre, - e _ (2-64 caratteri)'),
  description: z.string().min(1).max(500),
  content: z.string().min(1).max(PA_SKILL_MAX_CONTENT_CHARS),
  visibility: PaSkillVisibilitySchema.default('personal'),
  /** L'editor del workspace crea skill già attive; le bozze arrivano dalla chat. */
  status: PaSkillStatusSchema.default('active'),
  projectId: z.string().uuid().nullable().optional(),
});
export type PaSkillCreateRequest = z.infer<typeof PaSkillCreateRequestSchema>;

export const PaSkillUpdateRequestSchema = z.object({
  name: z.string().regex(PA_SKILL_NAME_REGEX).optional(),
  description: z.string().min(1).max(500).optional(),
  content: z.string().min(1).max(PA_SKILL_MAX_CONTENT_CHARS).optional(),
  visibility: PaSkillVisibilitySchema.optional(),
  projectId: z.string().uuid().nullable().optional(),
});
export type PaSkillUpdateRequest = z.infer<typeof PaSkillUpdateRequestSchema>;

/** `inherit` rimuove lo stato per-conversazione (torna al default). */
export const PaConversationSkillSetRequestSchema = z.object({
  state: z.enum(['active', 'disabled', 'inherit']),
});
export type PaConversationSkillSetRequest = z.infer<typeof PaConversationSkillSetRequestSchema>;
